import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "./Header";

const LINES = ["Truly independent.", "Advice that answers", "to no one but you."];

const lineVariants = {
  hidden: { y: "115%", rotate: 2 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.14 },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const IMAGE_REVEAL = {
  initial: { clipPath: "inset(100% 0% 0% 0%)" },
  animate: { clipPath: "inset(0% 0% 0% 0%)" },
  transition: { duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] },
};

const STATS = [
  { label: "Ownership", value: "Privately held — no bank, fund manager or insurer behind us." },
  { label: "Fees", value: "Fixed and agreed in advance — never a percentage of your assets." },
  { label: "Based in", value: "Canberra, serving clients across ACT, NSW and wider Australia." },
];

export const Hero = () => {
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgWrapRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-[#F9F8F4] pt-[96px] md:pt-[128px]">
      <div
        data-testid="hero-coming-soon-banner"
        className="relative mx-auto max-w-7xl px-6 md:px-10"
      >
        <div className="bg-[#0A2333] text-[#F9F8F4] px-8 md:px-14 py-10 md:py-14 border border-[#A78558]/40">
          <p className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-[#A78558]">
            Website in progress
          </p>
          <p className="mt-4 font-serif italic leading-[0.95] tracking-tight text-6xl md:text-8xl">
            Coming soon
          </p>
          <p
            data-testid="hero-coming-soon-date"
            className="mt-4 font-serif leading-tight tracking-tight text-4xl md:text-6xl text-[#A78558]"
          >
            End of November 2026
          </p>
          <p className="mt-6 text-xl md:text-3xl leading-snug text-[#F9F8F4]">
            Please contact{" "}
            <a
              href="mailto:hello@nuaprivatewealth.com.au"
              className="text-[#A78558] underline decoration-[#A78558]/50 underline-offset-4 hover:decoration-[#A78558] transition-colors"
              data-testid="hero-contact-email"
            >
              hello@nuaprivatewealth.com.au
            </a>{" "}
            for any queries
          </p>
        </div>
      </div>
      <div aria-hidden="true" className="pointer-events-none select-none absolute -bottom-4 left-0 w-full overflow-hidden">
        <span
          className="block whitespace-nowrap font-serif italic leading-none text-transparent text-[20vw]"
          style={{ WebkitTextStroke: "1px rgba(10,35,51,0.14)" }}
        >
          independent
        </span>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
        <div className="md:col-span-7 pt-10 md:pt-20">
          <motion.img
            src="/assets/nua-mark-transparent.png"
            alt=""
            aria-hidden="true"
            data-testid="hero-triskele"
            {...fade(0)}
            className="mb-8 h-10 md:h-12 w-auto opacity-90"
          />
          <h1
            data-testid="hero-heading"
            className="mt-2 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05] text-[#111413]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  style={{ transformOrigin: "0% 100%" }}
                >
                  {i === 2 ? (
                    <>
                      to no one <em className="italic text-[#0A2333]">but you.</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.h2
            data-testid="hero-subheading"
            {...fade(0.8)}
            className="mt-6 text-xs md:text-sm uppercase tracking-[0.2em] text-[#0A2333]"
          >
            Independent financial adviser in Canberra, Sydney, NSW and Australia wide
          </motion.h2>

          <motion.p
            data-testid="hero-subcopy"
            {...fade(0.85)}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
          >
            NUA Private Wealth is a privately owned, independent financial advice
            practice based in Canberra and serving surrounding regions. Nua — Irish for
            “new, fresh, modern” — is exactly how we think advice should work.
          </motion.p>

          <motion.p
            data-testid="hero-subcopy-clients"
            {...fade(0.88)}
            className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
          >
            At NUA Private Wealth, our clients' needs simply come first. Financial advice should be built around
            your goals, not someone else's interests. NUA Private Wealth provides truly
            independent, fixed-fee financial advice, with every recommendation shaped around your
            circumstances.
          </motion.p>

          <motion.p
            data-testid="hero-subcopy-2"
            {...fade(0.92)}
            className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
          >
            We have deliberately removed every conflict of interest that exists in the industry.
            No asset level based fees.
            No product sales targets. No commissions. No parent company influencing your
            decisions. Just advice that puts you first.
          </motion.p>

          <motion.p
            data-testid="hero-subcopy-3"
            {...fade(0.99)}
            className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
          >
            By replacing industry norms with total transparency, we ensure every strategy
            is custom-built around your goals, your family and your long-term security.
            Whether you are navigating complex Commonwealth superannuation schemes, selling a
            farm, thinking about investing, planning for a seamless retirement transition, or structuring a multi-entity estate, you
            receive objective, high-conviction advice at a fixed fee agreed upon before any
            work begins.
          </motion.p>

          <motion.p
            data-testid="hero-subcopy-4"
            {...fade(1.06)}
            className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
          >
            At NUA Private Wealth, fairness and total transparency are at the heart of
            everything we do. For individuals currently investing or receiving ongoing advice,
            we offer a complimentary “Advice fee health check” so you know exactly what you're
            paying and if it's fair versus the market.
          </motion.p>

          <motion.div data-testid="hero-cta-row" {...fade(1.0)} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollToId("contact")}
              className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
            >
              Book a free consultation
              <ArrowUpRight size={15} />
            </button>
            <Link
              to="/advice-fee-health-check"
              data-testid="hero-cta-fee-check"
              className="inline-flex items-center gap-2 rounded-full border border-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,color,transform] duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4] hover:-translate-y-0.5"
            >
              Advice fee health check
              <ArrowUpRight size={15} />
            </Link>
            <button
              data-testid="hero-cta-charter"
              onClick={() => scrollToId("charter")}
              className="inline-block relative after:absolute after:-inset-y-3 after:inset-x-0 after:content-[''] text-xs uppercase tracking-[0.15em] text-[#111413] underline decoration-[#A78558] decoration-2 underline-offset-8 hover:text-[#0A2333] transition-colors duration-300"
            >
              Read the Independence Charter
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            {...fade(1.15)}
            className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-black/10 pt-8 pb-10"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#A78558]">{s.label}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#4A524D]">{s.value}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="md:col-span-5 md:pt-12 pb-16 md:pb-24 relative">
          <motion.div
            ref={imgWrapRef}
            {...IMAGE_REVEAL}
            className="relative overflow-hidden rounded-t-[10rem] h-[52vh] md:h-[74vh] border border-black/10"
            data-testid="hero-image-frame"
          >
            <motion.img
              src="https://images.unsplash.com/photo-1766397218059-60b57f20b211?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxjYW5iZXJyYSUyMGxhbmRzY2FwZSUyMG1vcm5pbmd8ZW58MHx8fHwxNzg3OTA5NzgyfDA&ixlib=rb-4.1.0&q=85"
              alt="Mist rolling over the hills surrounding Canberra at first light"
              style={{ y }}
              className="absolute inset-0 h-[120%] w-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.p {...fade(1.3)} className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">
            The Canberra region at first light
          </motion.p>

          <motion.button
            {...fade(1.4)}
            data-testid="hero-scroll-hint"
            onClick={() => scrollToId("charter")}
            aria-label="Scroll to the Independence Charter"
            className="hidden md:flex absolute -left-14 bottom-24 h-12 w-12 items-center justify-center rounded-full border border-black/15 text-[#111413] transition-[background-color,color] duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4]"
          >
            <ArrowDown size={16} />
          </motion.button>
        </div>
      </div>
    </section>
  );
};
